import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  familyMeetingMinutes: [],
  currentFamilyMeetingMinute: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all family meeting minutes
export const fetchFamilyMeetingMinutes = createAsyncThunk(
  "familyMeetingMinutes/fetchFamilyMeetingMinutes",
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/family-meeting-minutes/${patientId}`
        : '/family-meeting-minutes';

      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Family Meeting Minutes"
      );
    }
  }
);

// Fetch single family meeting minute
export const fetchFamilyMeetingMinute = createAsyncThunk(
  "familyMeetingMinutes/fetchFamilyMeetingMinute",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/family-meeting-minutes/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Family Meeting Minute"
      );
    }
  }
);

// Create new family meeting minute
export const createFamilyMeetingMinute = createAsyncThunk(
  "familyMeetingMinutes/createFamilyMeetingMinute",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post(`/family-meeting-minutes`, data);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422)
        return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || "Failed to create Family Meeting Minute"
      );
    }
  }
);

// Update family meeting minute
export const updateFamilyMeetingMinute = createAsyncThunk(
  "familyMeetingMinutes/updateFamilyMeetingMinute",
  async ({ case_id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/family-meeting-minutes/case/${case_id}`, data);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422)
        return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || "Failed to update Family Meeting Minute"
      );
    }
  }
);

// Delete family meeting minute
export const deleteFamilyMeetingMinute = createAsyncThunk(
  "familyMeetingMinutes/deleteFamilyMeetingMinute",
  async (id, { getState, rejectWithValue }) => {
    try {
      await api.delete(`/family-meeting-minutes/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete Family Meeting Minute"
      );
    }
  }
);

// ==================== Slice ====================
const familyMeetingMinutesSlice = createSlice({
  name: "familyMeetingMinutes",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentFamilyMeetingMinute: (state, action) => {
      state.currentFamilyMeetingMinute = action.payload;
    },
    clearCurrentFamilyMeetingMinute: (state) => {
      state.currentFamilyMeetingMinute = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchFamilyMeetingMinutes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFamilyMeetingMinutes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.familyMeetingMinutes = action.payload;
      })
      .addCase(fetchFamilyMeetingMinutes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchFamilyMeetingMinute.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFamilyMeetingMinute.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentFamilyMeetingMinute = action.payload;
      })
      .addCase(fetchFamilyMeetingMinute.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createFamilyMeetingMinute.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createFamilyMeetingMinute.fulfilled, (state, action) => {
        state.isLoading = false;
        state.familyMeetingMinutes.unshift(action.payload);
      })
      .addCase(createFamilyMeetingMinute.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateFamilyMeetingMinute.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateFamilyMeetingMinute.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.familyMeetingMinutes.findIndex(
          (d) => d.id === updated.id
        );
        if (index !== -1) state.familyMeetingMinutes[index] = updated;

        if (state.currentFamilyMeetingMinute?.id === updated.id) {
          state.currentFamilyMeetingMinute = updated;
        }
      })
      .addCase(updateFamilyMeetingMinute.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteFamilyMeetingMinute.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteFamilyMeetingMinute.fulfilled, (state, action) => {
        state.isLoading = false;
        state.familyMeetingMinutes = state.familyMeetingMinutes.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentFamilyMeetingMinute?.id === action.payload) {
          state.currentFamilyMeetingMinute = null;
        }
      })
      .addCase(deleteFamilyMeetingMinute.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentFamilyMeetingMinute,
  clearCurrentFamilyMeetingMinute,
} = familyMeetingMinutesSlice.actions;

export default familyMeetingMinutesSlice.reducer;
